import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  SignalCellular4Bar as MobileIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  Public as PublicIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';

interface MobileOutageDetailsModalProps {
  open: boolean;
  onClose: () => void;
  outageId: string;
}

interface TimelineEntry {
  id: string;
  entry_type: string;
  timestamp: string;
  title?: string;
  description?: string;
  status?: string;
  source?: string;
  email_subject?: string;
  sequence_number?: number;
}

interface MobileOutageData {
  id: string;
  carrier_name: string;
  incident_id: string;
  status: string;
  severity: string;
  impact_description: string;
  affected_areas: string;
  start_time?: string;
  estimated_end_time?: string;
  resolved_time?: string;
  outage_start_time?: string;
  outage_end_time?: string;
  affected_service_type?: string;
  impact_level?: string;
  fault_location?: string;
  states?: string[];
  affected_customers: Array<{
    id: string;
    name: string;
    email: string;
    service_id: string;
  }>;
  notes?: string;
}

const MobileOutageDetailsModal: React.FC<MobileOutageDetailsModalProps> = ({
  open,
  onClose,
  outageId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<MobileOutageData | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);

  const fetchOutageDetails = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
        setError('');
      }
      const result = await apiService.getMobileOutageDetails(outageId);
      setData(result);
    } catch (err: any) {
      if (showLoading) {
        setError('Failed to load outage details');
      }
      console.error('Error fetching outage details:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [outageId]);

  const fetchTimeline = useCallback(async () => {
    try {
      const timelineData = await apiService.getMobileOutageTimeline(outageId);
      setTimeline(timelineData);
    } catch (err) {
      console.error('Error fetching timeline:', err);
    }
  }, [outageId]);

  useEffect(() => {
    if (open && outageId) {
      fetchOutageDetails();
      fetchTimeline();
    } else {
      // Reset data when modal closes
      setData(null);
      setTimeline([]);
    }
  }, [open, outageId, fetchOutageDetails, fetchTimeline]);

  // Auto-refresh when modal is open - check for updates every 30 seconds
  useEffect(() => {
    if (!open || !outageId) return;

    const refreshInterval = setInterval(() => {
      // Silently refresh data when modal is open to catch updates (without showing loading state)
      fetchOutageDetails(false);
      fetchTimeline();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [open, outageId, fetchOutageDetails, fetchTimeline]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      // Parse the UTC datetime and convert to Australian Eastern Time
      const utcDate = new Date(dateString);
      const sydneyDate = utcToZonedTime(utcDate, 'Australia/Sydney');
      return format(sydneyDate, 'dd/MM/yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      case 'monitoring': return 'warning';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'major': return 'warning';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getCleanDescription = (desc: string): string => {
    if (!desc) return '';
    
    // Look for "Description: <value>" pattern first (preferred format at end of email)
    const descColonMatch = desc.match(/^Description:\s*(.+)$/mi);
    if (descColonMatch) {
      return descColonMatch[1].trim();
    }
    
    // Look for "Description" followed by content on next lines (alternate format)
    const descMultilineMatch = desc.match(/^Description\s*\n(.+?)(?=\n\n|\nCarrier ID|\nStart Date|$)/mis);
    if (descMultilineMatch) {
      return descMultilineMatch[1].trim();
    }
    
    // Fallback to full description if no pattern found
    return desc;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MobileIcon color="primary" />
            <Typography variant="h6">Mobile Outage Details</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 1.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <Grid container spacing={2}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                {/* Status Overview */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Incident ID</Typography>
                      <Typography variant="body1" fontWeight="bold">{data.incident_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box mt={0.5}>
                        <Chip label={data.status} color={getStatusColor(data.status)} size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Severity</Typography>
                      <Box mt={0.5}>
                        <Chip label={data.severity} color={getSeverityColor(data.severity)} size="small" />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Timing Information */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight="bold">Timing</Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {(data.start_time || data.outage_start_time) && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Start Time</Typography>
                        <Typography variant="body2">{formatDateTime(data.start_time || data.outage_start_time)}</Typography>
                      </Grid>
                    )}
                    {data.estimated_end_time && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">ETR Date/Time</Typography>
                        <Typography variant="body2" fontWeight="medium" color="warning.main">{formatDateTime(data.estimated_end_time)}</Typography>
                      </Grid>
                    )}
                    {data.outage_end_time && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">End Time</Typography>
                        <Typography variant="body2">{formatDateTime(data.outage_end_time)}</Typography>
                      </Grid>
                    )}
                    {data.resolved_time && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Resolved</Typography>
                        <Typography variant="body2" color="success.main">{formatDateTime(data.resolved_time)}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Impact Details */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <WarningIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight="bold">Impact</Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {data.affected_service_type && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Services</Typography>
                        <Box mt={0.5}>
                          <Chip label={data.affected_service_type} color="primary" variant="outlined" size="small" />
                        </Box>
                      </Grid>
                    )}
                    {data.impact_level && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Level</Typography>
                        <Box mt={0.5}>
                          <Chip label={data.impact_level} color={getSeverityColor(data.impact_level)} variant="outlined" size="small" />
                        </Box>
                      </Grid>
                    )}
                    {data.fault_location && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          {data.fault_location}
                        </Typography>
                      </Grid>
                    )}
                    {data.states && data.states.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">States</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {data.states.map((state) => (
                            <Chip key={state} label={state} size="small" icon={<PublicIcon />} />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Description */}
                {data.impact_description && (
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Description</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getCleanDescription(data.impact_description)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                {/* Timeline */}
                {timeline && timeline.length > 0 && (
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      <TimelineIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight="bold">Timeline ({timeline.length})</Typography>
                    </Box>
                    <Box sx={{ maxHeight: 520, overflow: 'auto', pr: 1 }}>
                      <Stack spacing={1}>
                        {timeline.map((entry, index) => (
                          <Box
                            key={entry.id || index}
                            sx={{
                              p: 1.5,
                              backgroundColor: index === 0 ? 'primary.50' : 'grey.50',
                              borderLeft: '3px solid',
                              borderLeftColor: index === 0 ? 'primary.main' : 'grey.300',
                              borderRadius: 1,
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
                              <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, mr: 1 }}>
                                {entry.title || entry.email_subject || 'Update'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {formatDateTime(entry.timestamp)}
                              </Typography>
                            </Box>
                            {entry.status && (
                              <Chip label={entry.status} size="small" color={getStatusColor(entry.status)} sx={{ mb: 0.5 }} />
                            )}
                            {entry.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {entry.description}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                )}

                {/* Affected Customers */}
                {data.affected_customers && data.affected_customers.length > 0 && (
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Affected Customers ({data.affected_customers.length})
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      <Grid container spacing={1}>
                        {data.affected_customers.map((customer) => (
                          <Grid item xs={12} key={customer.id}>
                            <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight="medium">{customer.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{customer.service_id}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ py: 1 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MobileOutageDetailsModal;
